import { useEffect, useState } from "react";

import { useDebounce } from "@/hooks/useDebounce";
import type { AssetStatus, ViewerKind } from "@/lib/api";
import { availabilityLabel, viewerKindLabel } from "@/lib/format";
import { useFileManagerStore } from "@/state/fileManagerStore";

const STATUSES = ["ready", "queued", "rendering", "preparing", "error", "expired"] as const;
/** Os dois miolos do viewer, na ordem dos selos: `Doc`, `Package`. */
const VIEWER_KINDS = ["document", "packaging"] as const;

export function Filters() {
  const filters = useFileManagerStore((state) => state.filters);
  const setFilters = useFileManagerStore((state) => state.setFilters);
  const [searchInput, setSearchInput] = useState(filters.search);
  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      setFilters({ search: debouncedSearch });
    }
  }, [debouncedSearch, filters.search, setFilters]);

  const hasActiveFilters =
    filters.search || filters.status || filters.viewerKind || filters.hasNotes !== null;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <input
        type="search"
        placeholder="Buscar por nome..."
        value={searchInput}
        onChange={(event) => setSearchInput(event.target.value)}
        className="w-56 rounded border border-ink-700 bg-ink-800 px-3 py-1.5 text-xs placeholder:text-ink-500 focus:border-emerald-500 focus:outline-none"
        aria-label="Buscar por filename"
      />

      <label className="flex items-center gap-1.5 text-xs text-ink-300">
        Status
        <select
          value={filters.status ?? ""}
          onChange={(event) =>
            setFilters({ status: (event.target.value || null) as AssetStatus | null })
          }
          className="rounded border border-ink-700 bg-ink-800 px-2 py-1 text-xs"
        >
          <option value="">Todos</option>
          {STATUSES.map((status) => (
            <option key={status} value={status}>
              {availabilityLabel(status)}
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1.5 text-xs text-ink-300">
        Viewer
        <select
          value={filters.viewerKind ?? ""}
          onChange={(event) =>
            setFilters({ viewerKind: (event.target.value || null) as ViewerKind | null })
          }
          className="rounded border border-ink-700 bg-ink-800 px-2 py-1 text-xs"
          aria-label="Filtrar por viewer"
        >
          <option value="">Todos</option>
          {VIEWER_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {viewerKindLabel(kind)}
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1.5 text-xs text-ink-300">
        <input
          type="checkbox"
          checked={filters.hasNotes === true}
          onChange={(event) => setFilters({ hasNotes: event.target.checked ? true : null })}
          className="h-3.5 w-3.5 rounded border-ink-700 bg-ink-800"
        />
        Com notas
      </label>

      {hasActiveFilters && (
        <button
          type="button"
          onClick={() => {
            setSearchInput("");
            setFilters({ search: "", status: null, viewerKind: null, hasNotes: null });
          }}
          className="rounded border border-ink-700 px-2 py-1 text-xs text-ink-300 hover:bg-ink-800"
        >
          Limpar filtros
        </button>
      )}
    </div>
  );
}
